/**
 * components/riskTerminal/theme.js
 *
 * The Risk Terminal's look, ported as-is from the trader's Claude Design
 * source (handover/design/extracted/logic.js — money(), badge(), the shared
 * button/icon/popover styles). Every value here is the design's; nothing is
 * restyled. All styles are inline objects, so the warm-paper look stays
 * scoped to this screen.
 */

export const FONT = "'IBM Plex Sans',system-ui,sans-serif";

// Design law: colour means rule state, never money.
export const C = {
  ink: '#1a1917', ink2: '#3f3b35', ink3: '#585349', ink4: '#6b655c', ink5: '#878176',
  line: '#dbd6cb', line2: '#e5e1d8', line3: '#ccc5b8', line4: '#aba396',
  paper: '#efece6', paper2: '#f8f6f2', paper3: '#f1eee8',
  red: '#c1301a', redTint: '#fdeae5', redLine: '#eebcae',
  // upstox-055: CLOSED TODAY's background — history, so open risk stands out
  closedTint: '#f3f2ef', closedInk: '#6b655c',
  amber: '#8a5c07', amberTint: '#fdf3e0', amberLine: '#e2c48d', fadingTint: '#fdf8ee',
};

// upstox-055: the same tokens for a dark theme. The Risk Terminal has no dark
// mode yet (the screen and the app are light-only); these are the values to
// use when one lands, kept beside the light ones so the closed block's
// contrast is decided once.
export const C_DARK = { closedTint: '#24231f', closedInk: '#a59f94' };

export const HATCH = 'repeating-linear-gradient(135deg,#efece6 0 5px,#fff 5px 10px)';

export const btnGhost = {
  height: '27px', padding: '0 9px', background: '#fff', color: C.ink3, border: '1px solid ' + C.line3,
  fontFamily: FONT, fontSize: '10px', letterSpacing: '0.08em', cursor: 'pointer', flex: '0 0 auto',
};
export const btnRepair = {
  height: '24px', padding: '0 9px 0 6px', display: 'flex', alignItems: 'center', gap: '5px',
  background: '#fff', color: C.ink, border: '1px solid ' + C.amberLine, cursor: 'pointer',
  flex: '0 0 auto', fontFamily: FONT, fontSize: '10px', letterSpacing: '0.06em', fontWeight: 600,
};
export const iconBtnSm = {
  width: '26px', height: '26px', flex: '0 0 26px', padding: 0, display: 'flex', alignItems: 'center',
  justifyContent: 'center', background: 'transparent', border: '1px solid transparent', cursor: 'pointer',
};
export const icSm = {
  fontFamily: "'Material Symbols Outlined'", fontSize: '16px', lineHeight: 1, fontWeight: 200,
  color: C.ink5, fontVariationSettings: "'wght' 200, 'GRAD' -25, 'opsz' 20", fontStyle: 'normal',
  letterSpacing: 0, whiteSpace: 'nowrap', display: 'block',
};
export const popStyle = {
  position: 'absolute', top: 'calc(100% + 4px)', left: '12px', zIndex: 30, background: C.ink,
  color: '#fff', fontSize: '11px', padding: '7px 10px', whiteSpace: 'nowrap', letterSpacing: '0.02em',
  fontWeight: 400, pointerEvents: 'none',
};
export const textareaStyle = {
  background: '#fff', border: '1px solid ' + C.line4, color: C.ink, fontFamily: FONT, fontSize: '13px',
  padding: '9px 10px', outline: 'none', width: '100%', resize: 'none', lineHeight: 1.5,
};

// A control that exists in the design but is not wired yet: present, visibly
// inert, and its tooltip says why.
export const disabledLook = { opacity: 0.45, cursor: 'not-allowed' };
export const WIRED_NEXT = 'Not wired yet — arrives in the next update';
export const NO_ORDERS = 'This terminal places no orders yet — use the Upstox app';

const inrInt = n => Math.round(Math.abs(n)).toLocaleString('en-IN');
export const inr0 = n => '₹' + inrInt(n);

/** The design's money(): sign + ₹, three sizes (38/16/12). null → unknown, never 0. */
export function money(n, size) {
  if (n === null || n === undefined || Number.isNaN(n)) {
    return { text: '—', style: { fontSize: size + 'px', color: C.ink5, fontVariantNumeric: 'tabular-nums' }, unknown: true };
  }
  const text = (n < 0 ? '−₹' : '+₹') + inrInt(n);
  const color = n < 0 ? '#000000' : C.ink3;
  return { text, style: { fontSize: size + 'px', color, fontVariantNumeric: 'tabular-nums', letterSpacing: size > 24 ? '-0.02em' : '0', fontWeight: n < 0 ? 600 : 400 } };
}

export const signed = n => (n < 0 ? '−' : '+') + inrInt(n);

/** The design's badge(): aligned → no tag; fading amber text; confirmed red; else hatched. */
export function badge(kind, extra) {
  const base = { fontSize: '10px', letterSpacing: '0.1em', padding: '3px 7px', whiteSpace: 'nowrap', flex: '0 0 auto', fontWeight: 500 };
  if (kind === 'aligned') return { label: '', style: { display: 'none' } };
  if (kind === 'fading') return { label: 'AGAINST FADING TREND', style: { ...base, color: C.amber, padding: '3px 0', fontWeight: 600 } };
  if (kind === 'confirmed') return { label: 'AGAINST CONFIRMED TREND', style: { ...base, color: '#fff', background: C.red, border: '1px solid ' + C.red, fontWeight: 600 } };
  return { label: extra || 'NOT GRADED', style: { ...base, color: C.ink2, border: '1px dashed ' + C.ink5, backgroundImage: HATCH } };
}

/** upstox-074: "not graded" in plain grey caps — market closed, trend tied, holding, MCX. No hatch, no border. */
export const plainNotGraded = { fontSize: '10px', letterSpacing: '0.1em', padding: '3px 0', whiteSpace: 'nowrap', flex: '0 0 auto', fontWeight: 500, color: '#6b655c' };

/** A section the server could not compute: its reason, in the not-graded texture. */
export const notComputedStyle = {
  fontSize: '11px', letterSpacing: '0.06em', color: C.ink2, border: '1px dashed ' + C.ink5,
  backgroundImage: HATCH, padding: '8px 10px', lineHeight: 1.4,
};

export function hhmm(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
}

export function mmss(sec) {
  const t = Math.max(0, Math.round(sec));
  return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
}

// Icons: the design's Material Symbols (weight 200, grade −25, 20 px optical
// size), loaded once from Google Fonts, subset to the eight glyphs used.
let iconsLoaded = false;
export function loadIcons() {
  if (iconsLoaded || typeof document === 'undefined') return;
  iconsLoaded = true;
  const l = document.createElement('link');
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20,200,0,-25'
    + '&icon_names=chevron_right,close,drive_file_move,edit,group,inventory_2,keyboard_double_arrow_left,keyboard_double_arrow_right,link,logout,unfold_less,unfold_more&display=block';
  document.head.appendChild(l);
}
